/* eslint-disable @typescript-eslint/no-explicit-any */
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * A utility function to merge Tailwind classes with clsx.
 * @param inputs - The Tailwind classes to merge.
 * @returns The merged classes.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A utility function to create a unique key for react components.
 * @param prefix - The prefix to use for the key.
 */
export function createKey(prefix: string) {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

export const ensureArray = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

/**
 * Format a number as currency
 * @param amount - Number to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number | bigint) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(amount);
};


export const formatPrice = (amount: number, currency: string = "NGN") => {
  if (currency === "NGN") {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
};

export const getProductAvailability = (product: any) => {
  // Non-variant product
  if (!product.variants || product.variants.length === 0) {
    if (product.totalStock === null) return { hasStock: true, isUnlimited: true };
    return { hasStock: (product.totalStock || 0) > 0, isUnlimited: false };
  }

  // Variant-based product
  const hasAvailableVariant = product.variants.some(
    (v: any) => v.isActive && (v.stock === null || (v.stock || 0) > 0)
  );
  const hasUnlimitedVariant = product.variants.some(
    (v: any) => v.isActive && v.stock === null
  );

  return {
    hasStock: hasAvailableVariant,
    isUnlimited: hasUnlimitedVariant,
  };
};
